import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Bell, Check } from "lucide-react";

export default function NotificationBell() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef(null);

  const fetchN = useCallback(async () => {
    if (!user || user === false) return;
    try {
      const { data } = await api.get("/notifications");
      setItems(data.items || []);
      setUnread(data.unread || 0);
    } catch {}
  }, [user]);

  useEffect(() => {
    if (!user || user === false) {
      setItems([]); setUnread(0); return;
    }
    fetchN();
    const id = setInterval(fetchN, 30000);
    return () => clearInterval(id);
  }, [user, fetchN]);

  // close on outside click
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const markRead = async (id) => {
    setItems((list) => list.map((i) => i.id === id ? { ...i, read: true } : i));
    setUnread((n) => Math.max(0, n - 1));
    try { await api.post(`/notifications/${id}/read`); } catch {}
  };

  const markAll = async () => {
    setItems((list) => list.map((i) => ({ ...i, read: true })));
    setUnread(0);
    try { await api.post("/notifications/read-all"); } catch {}
  };

  if (!user || user === false) return null;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="relative w-9 h-9 rounded-full flex items-center justify-center border border-[#EAE5D9] hover:border-[#D96C4A] hover:text-[#D96C4A] transition-colors"
        data-testid="notification-bell"
        aria-label="Notifications"
      >
        <Bell size={16} />
        {unread > 0 && (
          <span
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#D96C4A] text-white text-[10px] font-medium inline-flex items-center justify-center"
            data-testid="notification-unread-count"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-3 w-[340px] sm:w-[380px] organic-card overflow-hidden shadow-xl z-50"
          data-testid="notification-dropdown"
        >
          <div className="px-4 py-3 border-b flex items-center justify-between" style={{ borderColor: "#EAE5D9" }}>
            <div className="text-sm font-medium text-[#2D3B31]">Notifications</div>
            {unread > 0 && (
              <button onClick={markAll} className="text-xs text-[#D96C4A] hover:underline inline-flex items-center gap-1" data-testid="notification-mark-all">
                <Check size={12} /> Mark all read
              </button>
            )}
          </div>
          <div className="max-h-[400px] overflow-y-auto">
            {items.length === 0 ? (
              <div className="p-8 text-center text-sm text-[#8A948C]">
                No notifications yet.
              </div>
            ) : (
              <ul className="divide-y" style={{ borderColor: "#EAE5D9" }}>
                {items.map((n) => (
                  <li key={n.id} className={`px-4 py-3 ${n.read ? "" : "bg-[#FAF6F2]"}`} data-testid={`notification-${n.id}`}>
                    <Link
                      to={n.link || "/"}
                      onClick={() => { setOpen(false); if (!n.read) markRead(n.id); }}
                      className="block hover:opacity-90"
                    >
                      <div className="flex items-start gap-2">
                        {!n.read && <span className="w-2 h-2 rounded-full bg-[#D96C4A] mt-1.5 shrink-0" />}
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-[#2D3B31]">{n.message}</div>
                          <div className="text-xs text-[#8A948C] mt-1">
                            {new Date(n.created_at).toLocaleString()}
                          </div>
                        </div>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
